import {
  Anchor,
  Badge,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { IconCalendar, IconTestPipe, IconUser } from '@tabler/icons-react';
import { Media, Patient, Specimen } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { useDateDisplay } from '@hooks/useDateDisplay';
import {
  SPECIMEN_CONDITION_I18N_KEYS,
  SPECIMEN_STATUS_I18N_KEYS,
} from '../constants/specimen.consts';
import { Link } from 'react-router-dom';
import { ARKON, IDENTIFIERS } from '@consts/systems.consts';
import { getPatientOrPractitionerName } from '../utils/common-helpers';
import classes from './SpecimenDisplayCard.module.css';
import { getSpecimenTypeDisplay } from '../utils/specimen-utils';
import { loadSpecimenPhotos } from '../utils/specimen-photo.helpers';
import EncounterImageCarousel from '../../patient/images-tab/EncounterImageCarousel';
interface SpecimenDisplayCardProps {
  specimen: Specimen;
  patient?: Patient;
  bg?: string;
  onView?: () => void;
}

export function SpecimenDisplayCard({
  specimen,
  patient,
  bg,
  onView,
}: SpecimenDisplayCardProps) {
  const { t } = useTranslation('lims');
  const formatDate = useDateDisplay();
  const medplum = useMedplum();
  const [specimenMedia, setSpecimenMedia] = useState<Media[]>([]);

  useEffect(() => {
    if (!specimen) {
      return;
    }
    loadSpecimenPhotos(medplum, specimen).then(({ existingMedia }) => {
      setSpecimenMedia(existingMedia);
    });
  }, [medplum, specimen]);

  const specimenId = specimen.identifier?.[0]?.value || specimen.id || 'N/A';
  const externalId = specimen.identifier?.find(
    (id) => id.system === IDENTIFIERS.SPECIMEN_EXTERNAL_ID
  )?.value;
  const specimenType =
    getSpecimenTypeDisplay(specimen) || t('specimen.unknownSampleType');
  const statusKey =
    (specimen.status && SPECIMEN_STATUS_I18N_KEYS[specimen.status]) ||
    'specimen.statuses.unknown';
  const statusLabel = String(t(statusKey as any));
  const conditionCode = specimen.condition?.[0]?.coding?.[0]?.code as
    | string
    | undefined;
  const qualityKey =
    (conditionCode && SPECIMEN_CONDITION_I18N_KEYS[conditionCode]) || undefined;
  const qualityLabel = qualityKey ? String(t(qualityKey as any)) : '';
  const qualityColor =
    conditionCode === ARKON.CS.SPECIMEN.CONDITION.CODES.SATISFACTORY
      ? 'green'
      : conditionCode === ARKON.CS.SPECIMEN.CONDITION.CODES.UNSATISFACTORY
        ? 'red'
        : 'gray';

  const collectionDate = specimen.collection?.collectedDateTime;
  const registrationDate = specimen.receivedTime;
  const notes = specimen.note?.[0]?.text;
  const qualityReason =
    conditionCode === ARKON.CS.SPECIMEN.CONDITION.CODES.UNSATISFACTORY
      ? specimen.condition?.[0]?.text
      : undefined;
  const isHighlighted =
    specimen.status === 'available' &&
    conditionCode === ARKON.CS.SPECIMEN.CONDITION.CODES.SATISFACTORY;

  return (
    <Paper
      shadow="sm"
      p="md"
      withBorder
      bg={bg ?? undefined}
      radius="md"
      opacity={isHighlighted ? 1 : 0.6}
      className={isHighlighted ? classes.highlighted : undefined}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Stack gap={0}>
              <Group gap="xs">
                <IconTestPipe size={16} />
                <Text fw={600}>{specimenId}</Text>
              </Group>
              <Text size="xs" c="dimmed">
                {specimenType}
              </Text>
            </Stack>
          </Group>
          <StatusBadge
            status={specimen.status || 'unknown'}
            label={statusLabel}
          />
        </Group>

        <SimpleGrid cols={{ base: 1, sm: externalId ? 3 : 4 }} spacing="md">
          {patient && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                {t('specimenDetails.patient')}
              </Text>
              <Group gap="xs">
                <IconUser size={14} />
                <Anchor component={Link} to={`/Patient/${patient.id}`}>
                  {getPatientOrPractitionerName(patient)}
                </Anchor>
              </Group>
            </Stack>
          )}

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              {t('specimenDetails.collectionDate')}
            </Text>
            <Group gap="xs">
              <IconCalendar size={14} />
              <Text>{collectionDate ? formatDate(collectionDate) : '-'}</Text>
            </Group>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              {t('specimenDetails.receivedDate')}
            </Text>
            <Group gap="xs">
              <IconCalendar size={14} />
              <Text>
                {registrationDate ? formatDate(registrationDate) : '-'}
              </Text>
            </Group>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              {t('specimenDetails.quality')}
            </Text>
            {conditionCode ? (
              <Stack gap={2}>
                <Badge variant="light" color={qualityColor}>
                  {qualityLabel}
                </Badge>
                {qualityReason && (
                  <Text size="xs" c="dimmed">
                    {qualityReason}
                  </Text>
                )}
              </Stack>
            ) : (
              <Text c="dimmed">-</Text>
            )}
          </Stack>

          {externalId && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                {t('specimenDetails.externalId')}
              </Text>
              <Text>{externalId}</Text>
            </Stack>
          )}
          {onView && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                {t('orderList.table.actions')}
              </Text>

              <Anchor onClick={onView}>{t('orderDetails.viewSpecimen')}</Anchor>
            </Stack>
          )}
        </SimpleGrid>

        <Group grow align="flex-start">
          {specimenMedia.length > 0 && (
            <Stack gap={0}>
              <Text size="xs" c="dimmed" tt="uppercase">
                {t('specimenDetails.photos')}
              </Text>
              <EncounterImageCarousel images={specimenMedia} />
            </Stack>
          )}

          {notes && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase">
                {t('specimenDetails.notes')}
              </Text>
              <Text size="sm">{notes}</Text>
            </Stack>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
